<template>
  <modal
    name="offerInStoreRedemption"
    modal-style="compact"
    @opened="onModalOpenedHandler"
    @closed="onModalClosedHandler"
  >
    <redeemable-offer
      v-if="offer"
      :offer="offer"
      :offer-redemption-code="offerRedemptionCode"
      :is-offer-redeemed="isOfferRedeemed"
      :is-loading-redemption-code="isLoadingRedemptionCode"
      :code-expiration-in-seconds="codeExpirationInSeconds"
    />

    <un-available-offer v-else />
  </modal>
</template>


<script>
  import { mapGetters } from 'vuex';
  import moment from 'moment-timezone';

  import errorHandlers from '@/services/error-handlers';
  import http from '@/services/http';

  import RedeemableOffer from './redeemable-offer/index.vue';
  import UnAvailableOffer from './unavailable-offer/index.vue';


  export default {
    name: 'OfferInStoreRedemptionModal',

    components: { RedeemableOffer, UnAvailableOffer },

    data() {
      return {
        offer: null,
        offerRedemptionCode: null,
        isOfferRedeemed: false,
        isLoadingRedemptionCode: false,
        codeExpirationInSeconds: 0,
        codeExpirationInterval: null,
        codeExpirationTimestamp: null,
        hasCheckinToken: false
      };
    },

    computed: {
      ...mapGetters('locations', ['timeZone']),
      ...mapGetters('merchant', ['merchant', 'shouldUseOfferKeyToken', 'shouldGenerateRedemptionCode'])
    },

    methods: {
      onModalOpenedHandler(data) {
        if (data.offer) {
          this.offer = data.offer;

          if (data.offer.checkinToken) {
            this.setCheckInToken(data);
          }
          else {
            this.loadOfferRedemptionCode();
          }
        }
      },

      onModalClosedHandler() {
        this.stopCodeExpirationTimer();

        this.offer = null;

        this.offerRedemptionCode = null;

        this.isOfferRedeemed = false;

        this.$store.dispatch('offers/getOffers', { silent: true });
      },

      setCheckInToken(data) {
        this.hasCheckinToken = true;

        this.offerRedemptionCode = data.offer.checkinToken;

        this.codeExpirationTimestamp = data.offer.redemptionEndDate;

        this.startCodeExpirationTimer();
      },

      async loadOfferRedemptionCode() {
        try {
          this.isLoadingRedemptionCode = true;

          this.stopCodeExpirationTimer();

          if (this.shouldUseOfferKeyToken && this.offer.offerKeyToken) {
            // Use offer key token directly - no API call needed for redemption code
            this.offerRedemptionCode = this.offer.offerKeyToken;

            this.codeExpirationTimestamp = moment().add(15, 'minutes').toISOString();

            this.startCodeExpirationTimer();
          }
          else if (this.shouldGenerateRedemptionCode) {
            // Legacy flow - generate redemption code
            const response = await http.post('/cardfree-api/v1/offer/me/redemption-codes', {
              ianaTimezoneId: this.timeZone,
              offerAllocationPublicId: this.offer.offerAllocationPublicId
            });

            this.offerRedemptionCode = response.data.checkinToken;

            this.codeExpirationTimestamp = response.data.redemptionEndDate;

            this.startCodeExpirationTimer();
          }
          else {
            this.$closeModal('offerInStoreRedemption');
          }
        }
        catch (error) {
          if (this.offerRedemptionCode) {
            errorHandlers.silent(error);

            this.isOfferRedeemed = true;

            this.stopCodeExpirationTimer();
          }
          else {
            errorHandlers.generic(error);

            this.$closeModal('offerInStoreRedemption');
          }
        }
        finally {
          this.isLoadingRedemptionCode = false;
        }
      },

      codeExpirationTimerTick() {
        const refreshQrCode = this.merchant.options.offersOptions?.inStoreRedemptionOptions?.refreshRedemptionCodeOnExpirationEnabled;

        this.codeExpirationInSeconds--;

        if (this.codeExpirationInSeconds === 0) {
          if (this.hasCheckinToken) {
            this.getCheckInToken();
          }
          else if (refreshQrCode) {
            this.loadOfferRedemptionCode();
          }
          else {
            this.$closeModal('offerInStoreRedemption');
          }
        }
      },

      async getCheckInToken() {
        try {
          this.isLoadingRedemptionCode = true;

          this.stopCodeExpirationTimer();

          const pendingOffer = await this.$store.dispatch('offers/convertPendingPoints', this.offer.conversionThresholdPublicId);

          this.offerRedemptionCode = pendingOffer.checkinToken;

          this.codeExpirationTimestamp = pendingOffer.redemptionEndDate;

          this.startCodeExpirationTimer();
        }
        catch (error) {
          errorHandlers.generic(error);

          this.$closeModal('offerInStoreRedemption');
        }
        finally {
          this.isLoadingRedemptionCode = false;
        }
      },

      startCodeExpirationTimer() {
        const now = moment();
        const expiration = moment(this.codeExpirationTimestamp);
        this.codeExpirationInSeconds = Math.ceil(expiration.diff(now, 'seconds', true));
        this.codeExpirationInterval = setInterval(this.codeExpirationTimerTick, 1000);
      },

      stopCodeExpirationTimer() {
        clearInterval(this.codeExpirationInterval);
      }
    }
  };
</script>
